/**
 * "Send this export to Dropbox?" — the confirmation between the Submit button
 * and the upload.
 *
 * Submitting is the one thing in the export dialogs that leaves the building:
 * the folder it writes into is the client's, shared with people who treat what
 * appears there as finished work. A download can be thrown away and nobody
 * sees it; this cannot. So it is a button of its own rather than a tick beside
 * Download, and it says where the files are going before it sends them.
 *
 * BOTH FOLDERS ARE PICKED HERE, and nothing about them is stored. Where a
 * playable belongs is an export-time decision: a game made for one company is
 * sometimes delivered to another, and the client's folder for it is named by
 * the client, not by us. The fields start on the obvious guess — the company
 * being worked in, the game being exported — and the lists beside them are the
 * folders Dropbox actually has, which is what makes the guess easy to correct.
 *
 * The lists come from Dropbox itself, a level at a time, so a folder added or
 * renamed on that side needs no change here. When Dropbox cannot be reached
 * the fields still take a typed name — the delivery is the server's business
 * and a list that failed to load is no reason to block it.
 */

import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { makeStyles } from "@material-ui/core/styles";
import { findDropboxGameFolder, listDropboxFolders } from "../modules/dropboxApi";

const useClasses = makeStyles({
  title: {
    textAlign: "center",
    padding: "16px 24px 8px",
  },
  intro: {
    textAlign: "center",
    marginBottom: "12px",
  },
  field: {
    marginBottom: "10px",
  },
  path: {
    backgroundColor: "#2a2a2a",
    borderRadius: "4px",
    padding: "10px 12px",
    color: "#ccc",
    fontSize: "0.85rem",
    fontFamily: "monospace",
    wordBreak: "break-all",
  },
  note: {
    marginTop: "12px",
    textAlign: "center",
    fontSize: "0.8rem",
    color: "#9a9a9a",
  },
  actions: {
    justifyContent: "center",
    padding: "16px",
  },
});

/**
 * The existing folder a dashboard name refers to, or "".
 *
 * Matched on letters and digits alone, in either direction, so "Yolo Game
 * Studio" finds "YOLO" and "Word Fusion" finds "Word Fusion". The longest
 * match wins: with folders named "WOW" and "WOWGuru", "WOW Guru" should not
 * settle for the shorter one.
 *
 * NO MATCH MEANS NO ANSWER, and the field stays empty rather than offering the
 * dashboard's own name. The two sides disagree often — the dashboard says
 * "WOW" where Dropbox says "Words Of Wonders" — and a name that looks filled
 * in is accepted without reading, which is how a second folder gets created
 * beside the right one.
 */
function matchFolder(folders, wanted) {
  const normalise = (value) => String(value || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  const name = normalise(wanted);
  if (!name) return "";

  return (folders || [])
    .filter((folder) => {
      const candidate = normalise(folder);
      return candidate && (name.startsWith(candidate) || candidate.startsWith(name));
    })
    .sort((a, b) => normalise(b).length - normalise(a).length)[0] || "";
}

export default function DialogDropboxSubmit({
  isOpen,
  defaultCompanyFolder,
  defaultGameFolder,
  gameCode,
  destinationFor,
  onConfirm,
  onCancel,
}) {
  const classes = useClasses();
  const defaultGame = defaultGameFolder || "";

  const [company, setCompany] = useState("");
  // Whether the game field still holds what the dialog put there. Once it is
  // typed in or picked, a late-arriving folder list must not overwrite it.
  const untouchedGame = useRef(true);
  const [game, setGame] = useState(defaultGame);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [gameOptions, setGameOptions] = useState([]);

  // Reset on every open: the folder is chosen for ONE export, and a choice
  // left over from the last one is the mistake this dialog exists to prevent.
  //
  // The publisher is filled in from the FOLDERS THAT EXIST, not from the
  // company's name: Dropbox calls them FUGO, YOLO and LEAP while the dashboard
  // says "Yolo Game Studio", and a name turned into a folder would quietly
  // create "YOLOGAMESTUDIO" beside the one that was meant. No match leaves the
  // field empty, which blocks Send until someone picks — the honest outcome
  // when we cannot tell which folder is right.
  useEffect(() => {
    if (!isOpen) return;
    setGame("");
    untouchedGame.current = true;
    listDropboxFolders().then((result) => {
      setCompanyOptions(result.folders);
      setCompany(matchFolder(result.folders, defaultCompanyFolder));
    });
  }, [isOpen, defaultCompanyFolder]);

  // The games on offer belong to whichever publisher is selected, so the list
  // follows the field above it. A publisher that is being typed matches
  // nothing until it is a real folder, which is the honest answer.
  useEffect(() => {
    if (!isOpen || !company.trim()) {
      setGameOptions([]);
      return;
    }
    let current = true;
    listDropboxFolders(company.trim()).then(async (result) => {
      if (!current) return;
      setGameOptions(result.folders);
      if (!untouchedGame.current) return;

      // The folder's own name first. Failing that, the one whose version
      // folders carry this game's code — the dashboard calls it "WOW" and
      // Dropbox calls it "Words Of Wonders", and only the contents connect
      // the two. Left empty when neither answers.
      const byName = matchFolder(result.folders, defaultGame);
      if (byName) {
        setGame(byName);
        return;
      }
      const byContents = await findDropboxGameFolder(company.trim(), gameCode);
      if (current && untouchedGame.current) setGame(byContents || "");
    });
    return () => {
      current = false;
    };
  }, [isOpen, company, defaultGame, gameCode]);

  const folder = [company.trim(), game.trim()].filter(Boolean).join("/");

  return (
    <Dialog open={isOpen} fullWidth maxWidth="xs" onClose={onCancel}>
      <DialogTitle className={classes.title}>Send to Dropbox?</DialogTitle>
      <DialogContent>
        <Typography className={classes.intro}>
          This export will be built and uploaded to the client&apos;s Dropbox.
        </Typography>
        <Autocomplete
          className={classes.field}
          freeSolo
          openOnFocus
          options={companyOptions}
          inputValue={company}
          onInputChange={(event, value, reason) => {
            // "reset" is the component reconciling itself — when the options
            // load, when a value is selected — and it arrives with an empty
            // string. Honouring it clears a field nobody touched.
            if (reason !== "reset") setCompany(value || "");
          }}
          onChange={(event, value) => setCompany(value || "")}
          renderInput={(params) => <TextField {...params} label="Company folder" size="small" />}
        />
        <Autocomplete
          className={classes.field}
          freeSolo
          openOnFocus
          options={gameOptions}
          inputValue={game}
          onInputChange={(event, value, reason) => {
            if (reason === "reset") return;
            untouchedGame.current = false;
            setGame(value || "");
          }}
          onChange={(event, value) => {
            untouchedGame.current = false;
            setGame(value || "");
          }}
          renderInput={(params) => <TextField {...params} label="Game folder" size="small" />}
        />
        <div className={classes.path}>{destinationFor(folder)}</div>
        <Typography className={classes.note}>
          Files already there under the same name are replaced. Nothing is deleted.
        </Typography>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          color="primary"
          variant="contained"
          disabled={!company.trim() || !game.trim()}
          onClick={() => onConfirm(folder)}
        >
          Send
        </Button>
        <Button
          variant="contained"
          style={{ backgroundColor: "#555", color: "#eee" }}
          onClick={onCancel}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}

DialogDropboxSubmit.propTypes = {
  isOpen: PropTypes.bool,
  /** The company being worked in; what the publisher field starts on. */
  defaultCompanyFolder: PropTypes.string,
  /** The game being exported; the first guess at its folder. */
  defaultGameFolder: PropTypes.string,
  /** The export's Game Title — the code the version folders are named with. */
  gameCode: PropTypes.string,
  /** Given the chosen folder, the full path the files will land in. */
  destinationFor: PropTypes.func.isRequired,
  /** Called with the chosen `"<publisher>/<game>"`. */
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};
